import React, { useState, useMemo } from 'react';
import { GameState, TransferPlayer, Player, TeamInLeague, League } from '../../domain/entities';
import { MAX_SQUAD_SIZE } from '../../domain/constants';
import { soundService } from '../../services/SoundService';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { View } from '../../App';
import { BuyPlayers } from './transfers/BuyPlayers';
import { SellPlayers } from './transfers/SellPlayers';
import { TransferHistory } from './transfers/TransferHistory';
import { FreeAgents } from './transfers/FreeAgents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import { TransfersHelpModal } from '../shared/TransfersHelpModal';

interface TransfersScreenProps {
    gameState: GameState;
    playerTeam: TeamInLeague;
    onNegotiatePurchase: (player: TransferPlayer, offerMultiplier: number) => Promise<{ success: boolean; message: string; acceptedOffer?: number; }>;
    onFinalizePurchase: (player: TransferPlayer, transferFee: number, wageMultiplier: number) => Promise<{ success: boolean; message: string; }>;
    onSubmitFreeAgentBid: (freeAgentId: number, wage: number) => Promise<{ success: boolean; message: string; }>;
    onSellPlayer: (player: Player) => Promise<{ success: boolean, message: string }>;
    onDismissPlayer?: (player: Player) => Promise<{ success: boolean, message: string }>;
    onReleasePlayer?: (player: Player) => Promise<{ success: boolean, message: string }>;
    pushView: (view: View) => void;
    adsEnabled: boolean;
    activeTab?: 'buy' | 'sell' | 'history';
    activeBuySubTab?: 'transfer_list' | 'free_agents' | 'scout';
    updateCurrentView: (propsToUpdate: Partial<View>) => void;
    filters?: any;
    filterWeek?: number;
    filterSeason?: number;
}

export const TransfersScreen: React.FC<TransfersScreenProps> = (props) => {
    const { gameState, playerTeam, updateCurrentView } = props;
    const [activeTab, setActiveTab] = useState<'buy' | 'sell' | 'history'>(props.activeTab || 'buy');
    const [activeBuySubTab, setActiveBuySubTab] = useState<'transfer_list' | 'free_agents' | 'scout'>(props.activeBuySubTab || 'transfer_list');
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Calculate counts for tab labels
    const { transferListCount, freeAgentsCount } = useMemo(() => {
        const playerTeamIds = new Set(playerTeam.squad.map(p => p.id));
        const listedPlayerIds = new Set(gameState.playersForSale);

        // Count transfer list (players on sale)
        const transferListCount = Array.from(listedPlayerIds).length;

        // Count free agents
        const freeAgentsCount = (gameState.freeAgents || []).length;

        return { transferListCount, freeAgentsCount };
    }, [gameState.playersForSale, gameState.freeAgents, playerTeam.squad]);

    const handleTabClick = (tab: 'buy' | 'sell' | 'history') => {
        soundService.playClick();
        setActiveTab(tab);
        updateCurrentView({ activeTab: tab });
    };

    const handleBuySubTabClick = (subTab: 'transfer_list' | 'free_agents' | 'scout') => {
        soundService.playClick();
        setActiveBuySubTab(subTab);
        updateCurrentView({ activeBuySubTab: subTab });
    }

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1><FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Transfers</h1>
                <button
                    className="button"
                    onClick={() => setIsHelpOpen(true)}
                    style={{ padding: '8px 12px', fontSize: '0.9em' }}
                    title="Help"
                >
                    Help
                </button>
            </div>
            <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                Buy and sell players to shape your squad. Manage your transfer budget and negotiate deals with other clubs.
            </p>

            <div className="tabs">
                <button className={`tab-button ${activeTab === 'buy' ? 'active' : ''}`} onClick={() => handleTabClick('buy')}>Buy Players</button>
                <button className={`tab-button ${activeTab === 'sell' ? 'active' : ''}`} onClick={() => handleTabClick('sell')}>Sell Players</button>
                <button className={`tab-button ${activeTab === 'history' ? 'active' : ''}`} onClick={() => handleTabClick('history')}>History</button>
            </div>

            {activeTab === 'buy' && (
                <div className="tabs" style={{ marginTop: '12px', borderTop: '1px solid var(--secondary-color)', paddingTop: '12px' }}>
                    <button className={`tab-button ${activeBuySubTab === 'transfer_list' ? 'active' : ''}`} onClick={() => handleBuySubTabClick('transfer_list')} style={{ fontSize: '0.9em' }}>Transfer List ({transferListCount})</button>
                    <button className={`tab-button ${activeBuySubTab === 'free_agents' ? 'active' : ''}`} onClick={() => handleBuySubTabClick('free_agents')} style={{ fontSize: '0.9em' }}>Free Agents ({freeAgentsCount})</button>
                    <button className={`tab-button ${activeBuySubTab === 'scout' ? 'active' : ''}`} onClick={() => handleBuySubTabClick('scout')} style={{ fontSize: '0.9em' }}>Scout Players</button>
                </div>
            )}

            <div className="finance-header" style={{
                backgroundColor: 'var(--primary-color)',
                padding: '8px 12px',
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <span>Transfer Budget: <strong className={playerTeam.finances.balance > 0 ? 'positive' : 'negative'}>£{playerTeam.finances.balance.toLocaleString()}</strong></span>
                <span>Squad Size: <strong>{playerTeam.squad.length} / {MAX_SQUAD_SIZE}</strong></span>
            </div>

            {props.adsEnabled && <AdPlaceholder />}

            {activeTab === 'buy' && activeBuySubTab === 'transfer_list' && <BuyPlayers
                gameState={gameState}
                playerTeam={playerTeam}
                onNegotiatePurchase={props.onNegotiatePurchase}
                onFinalizePurchase={props.onFinalizePurchase}
                pushView={props.pushView}
                filters={props.filters}
                mode="listed"
            />}
            {activeTab === 'buy' && activeBuySubTab === 'free_agents' && <FreeAgents
                gameState={gameState}
                playerTeam={playerTeam}
                onSubmitFreeAgentBid={props.onSubmitFreeAgentBid}
                pushView={props.pushView}
            />}
            {activeTab === 'buy' && activeBuySubTab === 'scout' && <BuyPlayers
                gameState={gameState}
                playerTeam={playerTeam}
                onNegotiatePurchase={props.onNegotiatePurchase}
                onFinalizePurchase={props.onFinalizePurchase}
                pushView={props.pushView}
                filters={props.filters}
                mode="scout"
            />}
            {activeTab === 'sell' && <SellPlayers
                playerTeam={playerTeam}
                gameState={gameState}
                onSellPlayer={props.onSellPlayer}
                onDismissPlayer={props.onDismissPlayer}
                pushView={props.pushView}
            />}
            {activeTab === 'history' && <TransferHistory history={gameState.transferHistory} playerTeamName={gameState.playerTeamName} pushView={props.pushView} gameState={gameState} filterWeek={props.filterWeek} filterSeason={props.filterSeason} />}

            <TransfersHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
        </>
    );
};
